import {
  type CreateInboxReplyDraftMutation,
  type CreateReplyDraftInputDto,
  type FetchResult,
  type MutationResult,
  useCreateInboxReplyDraftMutation,
} from "@qw/graphql";
import { useToast } from "@qw/ui";
import { useCallback } from "react";

import { type OpenDraftOptions, useDraftsCompose } from "./use-drafts-compose";

export type UseCreateReplyDraftReturn = [
  (
    input: CreateReplyDraftInputDto,
    options?: OpenDraftOptions
  ) => Promise<FetchResult<CreateInboxReplyDraftMutation> | undefined>,
  MutationResult<CreateInboxReplyDraftMutation>,
];

export const useCreateReplyDraft = (): UseCreateReplyDraftReturn => {
  const { toast } = useToast();
  const { openDraft } = useDraftsCompose();
  const [createReplyDraftMutation, result] = useCreateInboxReplyDraftMutation();

  const createReplyDraft = useCallback(
    (input: CreateReplyDraftInputDto, options?: OpenDraftOptions) =>
      createReplyDraftMutation({
        variables: { input },
        onCompleted: (data) => {
          if (!data?.createInboxReplyDraft) {
            throw new Error("Failed to create reply draft");
          }
          const openOptions: OpenDraftOptions = {
            sourceConversationId: options?.sourceConversationId ?? input.conversationId,
          };

          if (options?.placement) {
            openOptions.placement = options.placement;
          }

          openDraft(data.createInboxReplyDraft.id, openOptions);
        },
        onError: (error) => {
          toast({
            title: "Error creating reply draft",
            description: error instanceof Error ? error.message : "An unexpected error occurred",
            variant: "destructive",
          });
        },
        refetchQueries: ["InboxDrafts", "InboxConversations", "InboxDraftsCount"],
      }),
    [createReplyDraftMutation, toast, openDraft]
  );

  return [createReplyDraft, result];
};
